import { View, Text } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import type { BreedRanking, Dog, DogStats } from "../app/lib/types";
import { findAkcMatch } from "../app/lib/akc-match";

interface BreedStandingsProps {
  rankings: BreedRanking[];
  dog: Dog;
  stats: DogStats | null;
  hypotheticalRank: { rank: number; total: number } | null;
}

interface StandingRow {
  rank: number | string;
  dogName: string;
  sex: string;
  speed: string;
  isUser: boolean;
}

export function BreedStandings({ rankings, dog, stats, hypotheticalRank }: BreedStandingsProps) {
  if (rankings.length === 0 || !dog.breed) return null;

  const userSpeed = stats?.bestThreeAvgSpeed ?? 0;
  const sexLower = dog.sex?.toLowerCase() || "";
  const userSex = sexLower.startsWith("female") || sexLower === "bitch" ? "Bitch" : sexLower.startsWith("male") || sexLower === "dog" ? "Dog" : dog.sex || "—";

  // Match user's registered name against AKC rankings. The util handles case,
  // curly vs straight apostrophes, whitespace, leading champion prefix (CH/GCH),
  // and trailing title suffixes (FCAT/BCAT/etc) on both sides.
  const matchedAkcEntry = findAkcMatch(rankings, dog.registeredName);

  // Determine the user's "official" rank: matched entry's rank if found, otherwise hypothetical projection
  const officialRank = matchedAkcEntry?.rank ?? null;
  const projectedRank = hypotheticalRank?.rank ?? null;
  const userRank = officialRank ?? projectedRank ?? null;

  // Build top 5 rows, marking the user's matched row instead of duplicating it
  const top5 = rankings.slice(0, 5);
  const rows: StandingRow[] = top5.map((r) => ({
    rank: r.rank,
    dogName: r.dogName,
    sex: r.owner || "—",
    speed: r.speed != null && r.speed > 0 ? r.speed.toFixed(2) : "—",
    isUser: matchedAkcEntry?.rank === r.rank,
  }));

  // User's projected row (only used when dog is NOT already in AKC's published list)
  const userRow: StandingRow = {
    rank: userRank != null ? userRank : "N/R",
    dogName: dog.registeredName ? `${dog.registeredName} - ${dog.name}` : dog.name,
    sex: userSex,
    speed: userSpeed > 0 ? userSpeed.toFixed(2) : "—",
    isUser: true,
  };

  const userIsAkcRanked = !!matchedAkcEntry;
  const userInTop5 = userRank != null && userRank <= 5;

  // If user is NOT in AKC list AND projected into top 5, insert their projected row
  if (!userIsAkcRanked && userInTop5 && userRank != null) {
    rows.splice(userRank - 1, 0, userRow);
    if (rows.length > 6) rows.length = 6;
  }

  const currentYear = new Date().getFullYear();

  return (
    <View
      style={{
        width: "100%",
        alignSelf: "stretch",
        backgroundColor: "#ffffff",
        borderRadius: 14,
        borderWidth: 1,
        borderColor: "#e5e7eb",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 2,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      {/* Section title */}
      <View style={{ paddingHorizontal: 20, paddingTop: 20, paddingBottom: 14 }}>
        <Text style={{ fontSize: 16, fontWeight: "700", color: "#374151" }}>
          {currentYear} {dog.breed} Top 5 Standings
        </Text>
      </View>

      {/* Table header */}
      <View
        style={{
          flexDirection: "row",
          backgroundColor: "#1B2A4A",
          paddingVertical: 10,
          paddingHorizontal: 16,
        }}
      >
        <Text style={{ width: 52, fontSize: 12, fontWeight: "700", color: "#ffffff" }}>#</Text>
        <Text style={{ flex: 1, fontSize: 12, fontWeight: "700", color: "#ffffff" }}>Dog Name</Text>
        <Text style={{ width: 60, fontSize: 12, fontWeight: "700", color: "#ffffff", textAlign: "center" }}>Sex</Text>
        <Text style={{ width: 65, fontSize: 12, fontWeight: "700", color: "#ffffff", textAlign: "right" }}>MPH</Text>
      </View>

      {/* Table rows */}
      {rows.map((row, i) => (
        <View
          key={`row-${i}`}
          style={{
            flexDirection: "row",
            paddingVertical: 12,
            paddingHorizontal: 16,
            backgroundColor: row.isUser ? "#E0E7F2" : i % 2 === 0 ? "#ffffff" : "#f9fafb",
            borderLeftWidth: row.isUser ? 4 : 0,
            borderLeftColor: row.isUser ? "#1B2A4A" : "transparent",
            alignItems: "center",
          }}
        >
          <Text
            style={{
              width: 52,
              fontSize: 14,
              fontWeight: row.isUser ? "800" : "600",
              color: row.isUser ? "#1B2A4A" : "#374151",
            }}
            numberOfLines={1}
          >
            {row.rank}
          </Text>
          <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 6, overflow: "hidden" }}>
            <Text
              style={{
                flex: 1,
                fontSize: 14,
                fontWeight: row.isUser ? "800" : "500",
                color: row.isUser ? "#1B2A4A" : "#374151",
              }}
              numberOfLines={1}
            >
              {row.dogName}
            </Text>
            {row.isUser && (
              <View style={{ backgroundColor: "#1B2A4A", paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4 }}>
                <Text style={{ fontSize: 9, fontWeight: "800", color: "#ffffff" }}>YOU</Text>
              </View>
            )}
          </View>
          <Text
            style={{
              width: 60,
              fontSize: 13,
              color: row.isUser ? "#1B2A4A" : "#6b7280",
              textAlign: "center",
              fontWeight: row.isUser ? "700" : "400",
            }}
          >
            {row.sex}
          </Text>
          <Text
            style={{
              width: 65,
              fontSize: 14,
              fontWeight: row.isUser ? "800" : "600",
              color: row.isUser ? "#B45309" : "#F5A623",
              textAlign: "right",
            }}
          >
            {row.speed}
          </Text>
        </View>
      ))}

      {/* User row(s) if outside top 5 — show AKC official + projected when both are meaningful */}
      {!userInTop5 && (() => {
        const akcRow = userIsAkcRanked ? {
          rank: matchedAkcEntry!.rank,
          dogName: matchedAkcEntry!.dogName,
          sex: matchedAkcEntry!.owner || userSex,
          speed: matchedAkcEntry!.speed != null && matchedAkcEntry!.speed > 0 ? matchedAkcEntry!.speed.toFixed(2) : "—",
          subText: "Current rank — official AKC standing",
          isProjected: false,
        } : null;
        const projRow = (projectedRank != null && userSpeed > 0 && (!akcRow || projectedRank !== akcRow.rank)) ? {
          rank: projectedRank,
          dogName: dog.registeredName ? `${dog.registeredName} - ${dog.name}` : dog.name,
          sex: userSex,
          speed: userSpeed.toFixed(2),
          subText: "Projected rank — based on your best 3 avg",
          isProjected: true,
        } : null;
        const rowsToRender = ([akcRow, projRow].filter(Boolean) as Array<NonNullable<typeof akcRow>>)
          .sort((a, b) => a.rank - b.rank); // lower rank number = better, shows on top
        if (rowsToRender.length === 0) {
          // Fallback: dog not in AKC list and no projection — show old projected row as before
          rowsToRender.push({ ...userRow, subText: null, isProjected: true } as any);
        }
        return rowsToRender.map((displayRow, i) => {
          const isProjected = displayRow.isProjected;
          // Current rank (AKC official) = light navy brand palette, Projected = gold/yellow
          const bg = isProjected ? "#FEF3C7" : "#E0E7F2";
          const border = isProjected ? "#F59E0B" : "#1B2A4A";
          const badgeBg = isProjected ? "#F59E0B" : "#1B2A4A";
          const speedColor = isProjected ? "#B45309" : "#1B2A4A";
          const subColor = isProjected ? "#B45309" : "#1B2A4A";
          return (
          <View
            key={`user-row-${i}`}
            style={{
              paddingVertical: 12,
              paddingHorizontal: 16,
              backgroundColor: bg,
              borderLeftWidth: 4,
              borderLeftColor: border,
              borderTopWidth: i > 0 ? 1 : 0,
              borderTopColor: "#e5e7eb",
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Text style={{ width: 52, fontSize: 14, fontWeight: "800", color: "#1B2A4A" }} numberOfLines={1}>
                {`#${displayRow.rank}`}
              </Text>
              <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 6, overflow: "hidden" }}>
                <Text style={{ flex: 1, fontSize: 14, fontWeight: "800", color: "#1B2A4A" }} numberOfLines={1}>
                  {displayRow.dogName}
                </Text>
                <View style={{ backgroundColor: badgeBg, paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4 }}>
                  <Text style={{ fontSize: 9, fontWeight: "800", color: "#ffffff" }}>YOU</Text>
                </View>
              </View>
              <Text style={{ width: 60, fontSize: 13, color: "#1B2A4A", textAlign: "center", fontWeight: "700" }}>
                {displayRow.sex}
              </Text>
              <Text style={{ width: 65, fontSize: 14, fontWeight: "800", color: speedColor, textAlign: "right" }}>
                {displayRow.speed}
              </Text>
            </View>
            {displayRow.subText && (
              <Text style={{ fontSize: 11, color: subColor, fontStyle: "italic", marginTop: 4, marginLeft: 52 }}>
                {displayRow.subText}
              </Text>
            )}
          </View>
          );
        });
      })()}

      {/* Disclaimer — wording depends on whether dog is officially ranked or projected */}
      <View style={{ width: "100%", paddingHorizontal: 16, paddingVertical: 12, backgroundColor: "#f9fafb", borderTopWidth: 1, borderTopColor: "#f3f4f6" }}>
        <Text style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic", lineHeight: 16, flexShrink: 1, flexWrap: "wrap" }}>
          {userIsAkcRanked
            ? `Rank pulled from AKC's official ${currentYear} Fast CAT standings for ${dog.breed}.`
            : `Your dog's projected ranking is based on their best 3 average speed compared to the current ${currentYear} AKC Fast CAT rankings for ${dog.breed}. This is not an official AKC ranking.`}
        </Text>
      </View>
    </View>
  );
}
